import { useState, useEffect } from 'react';
import api from '../services/api';
import { FiDatabase, FiPlus, FiRotateCcw, FiTrash2, FiClock, FiUploadCloud, FiGitBranch } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import './Pages.css';

const Backups = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deployStatus, setDeployStatus] = useState(null);
  const [deploying, setDeploying] = useState(false);

  const fetchBackups = async () => {
    try {
      const { data } = await api.get('/backups');
      setBackups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Backup fetch error:', err);
      setBackups([]);
    } finally { setLoading(false); }
  };

  const fetchDeployStatus = async () => {
    try {
      const { data } = await api.get('/deploy/status');
      setDeployStatus(data);
    } catch { /* ignored */ }
  };

  useEffect(() => { fetchBackups(); fetchDeployStatus(); }, []);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => new Date(iso).toLocaleString('ka-GE', { hour12: false });

  const createBackup = async () => {
    setCreating(true);
    try {
      await api.post('/backups');
      fetchBackups();
    } catch { /* ignored */ }
    finally { setCreating(false); }
  };

  const restoreBackup = (b) => {
    setConfirmAction({
      title: 'ბაზის აღდგენა',
      message: `ნამდვილად გსურთ ბაზის აღდგენა "${b.filename}" ბექაფიდან?\n\nმიმდინარე მონაცემები წაიშლება და ჩანაცვლდება ბექაფის მონაცემებით!`,
      type: 'delete',
      confirmText: 'აღდგენა',
      onConfirm: async () => {
        try {
          await api.post(`/backups/restore/${b.filename}`);
          setConfirmAction({
            title: 'წარმატება',
            message: 'ბაზა წარმატებით აღდგა. გვერდი გადაიტვირთება.',
            type: 'warning',
            confirmText: 'კარგი',
            onConfirm: () => window.location.reload(),
          });
        } catch {
          setConfirmAction({
            title: 'შეცდომა', message: 'აღდგენა ვერ მოხერხდა', type: 'delete',
            confirmText: 'კარგი', onConfirm: () => setConfirmAction(null),
          });
        }
      },
    });
  };

  const deleteBackup = (b) => {
    setConfirmAction({
      title: 'ბექაფის წაშლა',
      message: `ნამდვილად გსურთ "${b.filename}" წაშლა?`,
      type: 'delete', confirmText: 'წაშლა',
      onConfirm: async () => {
        try { await api.delete(`/backups/${b.filename}`); fetchBackups(); } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const runDeploy = () => {
    setConfirmAction({
      title: 'დიფლოი',
      message: 'GitLab-იდან ბოლო ცვლილებები ჩამოიტვირთება, აიბილდება და სერვერი გადაიტვირთება. გსურთ გაგრძელება?',
      type: 'warning', confirmText: 'დიფლოი',
      onConfirm: async () => {
        setConfirmAction(null);
        setDeploying(true);
        try {
          const { data } = await api.post('/deploy');
          setDeploying(false);
          setConfirmAction({
            title: data.success ? 'წარმატება' : 'შეცდომა',
            message: data.success ? 'დიფლოი წარმატებით დასრულდა. სერვერი გადაიტვირთება.' : 'დიფლოი ვერ მოხერხდა.',
            type: data.success ? 'warning' : 'delete',
            confirmText: 'კარგი',
            onConfirm: () => { setConfirmAction(null); if (data.success) setTimeout(() => window.location.reload(), 3000); },
          });
          fetchDeployStatus();
        } catch {
          setDeploying(false);
          setConfirmAction({
            title: 'შეცდომა', message: 'დიფლოი ვერ მოხერხდა', type: 'delete',
            confirmText: 'კარგი', onConfirm: () => setConfirmAction(null),
          });
        }
      },
    });
  };

  return (
    <div className="page">
      {/* Deploy section */}
      <div className="page-header">
        <div>
          <h1>დიფლოი</h1>
          <p className="page-subtitle">
            {deployStatus?.lastCommit
              ? `ბოლო კომიტი: ${deployStatus.lastCommit.hash} — ${deployStatus.lastCommit.message}`
              : 'სტატუსი იტვირთება...'}
          </p>
        </div>
        <button className="btn-primary" onClick={runDeploy} disabled={deploying}>
          <FiUploadCloud /> {deploying ? 'მიმდინარეობს...' : 'დიფლოი'}
        </button>
      </div>

      {deployStatus?.lastCommit && (
        <div className="backup-card" style={{ marginBottom: 24 }}>
          <FiGitBranch style={{ color: '#667eea', fontSize: 20, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{deployStatus.branch}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{deployStatus.lastCommit.date}</div>
          </div>
          <span className="badge badge-purple">{deployStatus.lastCommit.hash}</span>
        </div>
      )}

      {/* Backup section */}
      <div className="page-header" style={{ marginTop: 8 }}>
        <div>
          <h1>ბექაფი</h1>
          <p className="page-subtitle">ავტომატური ბექაფი ყოველ ღამე 04:00-ზე (ბოლო 48 საათი)</p>
        </div>
        <button className="btn-primary" onClick={createBackup} disabled={creating}>
          <FiPlus /> {creating ? 'იქმნება...' : 'ახალი ბექაფი'}
        </button>
      </div>

      {loading ? (
        <div className="loading">იტვირთება...</div>
      ) : (
        <div className="backup-list">
          {backups.map((b) => (
            <div key={b.filename} className="backup-card">
              <div style={{ color: '#667eea', fontSize: 24, flexShrink: 0 }}><FiDatabase /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', wordBreak: 'break-all' }}>{b.filename}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}><FiClock style={{ fontSize: 11 }} /> {formatDate(b.created_at)}</div>
              </div>
              <span className="badge badge-blue">{formatSize(b.size)}</span>
              <button className="btn-icon btn-edit" onClick={() => restoreBackup(b)} title="აღდგენა"><FiRotateCcw /></button>
              <button className="btn-icon btn-danger" onClick={() => deleteBackup(b)} title="წაშლა"><FiTrash2 /></button>
            </div>
          ))}
          {backups.length === 0 && <div className="empty-state">ბექაფი არ არის</div>}
        </div>
      )}

      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default Backups;
